import styled from "styled-components";
import { ColoredSpan, SmallText, Title } from "../../components/shared/Texts";
import compasBg from '../../assets/images/compas/compasMain.webp';
import compasArrow from '../../assets/images/compas/compasArrow.webp';
import { media } from "../../styles/media";
import { useRef, useState } from "react";
import { AnimatePresence, motion, useMotionValueEvent, useScroll } from "framer-motion";

import { calculateAngle } from "../../utils/calculateAngle";
import { Stones } from "./Stones";
import { JobsBlock } from "./JobsBlock";
import { Clouds } from "./Clouds";
import { openVk } from "../../utils/openVk";
import {keyframes} from 'styled-components';

const Wrapper = styled.div`
    display: none;
    width: 100%;
    height: 100%;
    position: relative;
    z-index: 3;
    overflow-x: hidden;
    overflow-y: auto;

    ${media.desktop`
        display: flex;
        flex-direction: column;
    `}
`;

const TitleStyled = styled(motion.h3)`
    line-height: 100%;
    white-space: pre-line;
    color: var(--color-white-text);
    position: sticky;
    float: left;
    text-align: left;
    top: 50px;
    padding-left: 50px;
    font-size: 55px;
    width: min-content;
    z-index: 320;
    cursor: default;

    @media screen and (max-height: 920px) {
        padding-left: 45px;
        font-size: 50px;
    }
    @media screen and (max-height: 1440px) {
        padding-left: 40px;
        font-size: 50px;
    }
`;

const CompasWrapper = styled(motion.div)`
    position: fixed;
    top: -25px;
    left: 50%;
    transform: translateX(-50%);
    width: 357px;
    height: 357px;
    z-index: 100;
    pointer-events: none;

    display: none;

    ${media.desktop`
        display: block;
    `}
`;

const CompasElement = styled(motion.img)`
    object-fit: contain;
    width: 100%;
    height: 100%;
`;

const CompasArrow = styled(CompasElement)`
    position: absolute;
    inset: 0;
    z-index: 2;
`;

const CompasBlock = styled(motion.div)`
    position: absolute;
    top: 240px;
    left: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-direction: column;
    border: 1px solid var(--color-white);
    width: 100%;
    border-radius: 30px;
    text-align: center;

    ${media.desktop`
        padding: 45px 33px 39px;
        background-color: var(--color-white);
        max-width: 520px;
        margin: 0 auto;
    `}

    & ${SmallText} {
        max-width: 390px;
    }
`;

const CompasHeader = styled(Title)`
    margin-bottom: 25px;
    
    ${media.desktop`
        font-size: 18px;
    `}
`;

const TipBlock = styled(motion.div)`
    display: flex;
    align-items: center;
    justify-content: center;
    position: absolute;
    left: 0;
    top: 500px;

    width: 100%;

    gap: 15px;

    & p {
        color: var(--color-white-text);
        width: max-content;
    }
`;

const BotBlock = styled(motion.div)`
    width: 250px;
    max-width: 400px;
    margin-left: auto;
    height: auto;
    right: 0;
    overflow: hidden;
    padding: 25px 29px;
    border: 1px solid var(--color-white);
    border-radius: ${({$radius = 25}) => $radius}px;
    border-top-right-radius: 0;
    border-bottom-right-radius: 0;
    border-right: none;
    background-color: var(--color-gray);
    z-index: 13;
    transform: translateZ(0);

    display: none;

    ${media.desktop`
        display: block;
    `}

    & ${SmallText} {
        color: var(--color-white);
    }
`;

const LogoBlock = styled(BotBlock)`
    padding: 0 29px;
    display: flex;
    align-items: center;
    height: 89px;
    margin-bottom: 15px;

    display: none;

    ${media.desktop`
         display: flex;
    `}
`;

const FooterText = styled(SmallText)`
    position: absolute;
    top: 1860px;
    left: 60px;
    text-align: center;
    margin-top: auto;
    text-transform: none;
    color: var(--color-white-text);
    padding-bottom: 35px;
`;

const BotFooterText = styled.div`
    position: absolute;
   display: flex;
   align-items: center;
   top: 1860px;
   right: 60px;
   padding-bottom: 29px;
   gap: 10px;
   
   & ${SmallText} {
    color: var(--color-white-text);
   }

   & button {
        background-color: transparent;
   }
`;

const ContentWrapper = styled.div`
    position: absolute;
    height: 100%;
    width: 100%;
    top: 0;
    left: 50%;
    transform: translateX(-50%);
    max-width: 1440px;
`;

const BotWrapper = styled(motion.div)`
    position: fixed;
    right: 0;
    top: 32px;
    width: fit-content;
    margin-left: auto;
    z-index: 26;
`;

const CompasLogoBlock = styled(LogoBlock)`
    position: sticky;
    padding: 0 27px 0 77px;
    width: 308px;
    height: 100px;

    & p {
        font-size: 32px;
        line-height: 95%;
        color: var(--color-white-text);
    }
`;

const moveDown = keyframes`
    0% { transform: translateY(0); }
    50% { transform: translateY(10px); }
    100% { transform: translateY(0); }
`;

const Arrow = styled.svg`
    animation: ${moveDown} 1s infinite;
    animation-timing-function: ease-in-out;
`;

const COMPAS_SIZE_KOEF = 177 / 357;

const START_ANIMATION = 20;

export const MainPageDesktop = () => {
    const titleRef = useRef();
    const compasRef = useRef();
    const wrapperRef = useRef();

    const { scrollY } = useScroll({ container: wrapperRef });
    const [isFixed, setIsFixed] = useState(false);
    const [arrowDeg, setArrowDeg] = useState();

    useMotionValueEvent(scrollY, "change", (latest) => {
        setIsFixed(latest > START_ANIMATION);
    });

    const handleMouseEnter = (e) => {
        const {x = 0, y = 0, width: compassWidth = 0, height: compassHeight = 0} = compasRef?.current?.getBoundingClientRect() ?? {};

        const compassCenter = {
            x: x + compassWidth / 2,
            y: y + compassHeight / 2,
        }


        const {angleDeg} = calculateAngle(e.pageX, e.pageY, compassCenter)

        const newAngle = angleDeg - 130;

        setArrowDeg(newAngle);
    }

    const handleMouseLeave = () => {
        setArrowDeg(0);
    }

    const animationProps = {
        animate: isFixed ? {y: -460} : {},
        transition: {duration: 0.4, ease: 'linear'}
    }

    return (
        <>
            <CompasWrapper
                ref={compasRef}
                initial={{x: '-50%'}}
                animate={isFixed ? {y: -(357 / 2.3 * COMPAS_SIZE_KOEF), x: (window.innerWidth / 2) - 485, scale: COMPAS_SIZE_KOEF,} : {x: '-50%'}}
                transition={{
                    duration: 0.25,
                    ease: 'linear',
                    layout: true,
                }}
            >
                <CompasElement key="background" src={compasBg} alt="" />
                <CompasArrow key="arrow" src={compasArrow} alt="" animate={{rotate: arrowDeg}}/>
            </CompasWrapper>
            <BotWrapper>
                    <AnimatePresence>
                        {isFixed && (
                            <CompasLogoBlock 
                                initial={{opacity: 0, height: 0, x: 308}} 
                                exit={{opacity: 0, height: 0, x: 380}} 
                                animate={{ opacity: 1, height: 100, x: 0}} 
                                transition={{ x: {duration: 0.25, ease: 'linear'}, opacity: {duration: 0.15, ease: 'linear'}, height: {duration: 0.25, ease: 'linear'}}}
                            >
                                <p>
                                    <ColoredSpan>навигатор</ColoredSpan>{'\n'}профессий
                                </p>
                            </CompasLogoBlock>
                        )}
                    </AnimatePresence>
                    <LogoBlock layout={true}>
                        <svg width="148" height="45" viewBox="0 0 148 45" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <g clipPath="url(#clip0_3441_155493)">
                                <path d="M0 17.1493H5.75794L5.5511 18.2539H1.32314V21.878H4.8363V22.9671H1.32314V28.276H0V17.1493Z" fill="#FFF2EC" />
                                <path d="M10.4336 17.0996H11.7263V24.2361C11.7151 24.8082 11.7528 25.3802 11.8389 25.9457C12.0305 26.8238 12.8274 27.3109 14.0563 27.3109C14.9688 27.3109 15.6532 27.0689 15.9877 26.6128C16.3223 26.1567 16.4014 25.7502 16.4014 24.7108V17.0996H17.6941V24.8566C17.6941 26.4546 17.536 26.9883 16.8637 27.5933C16.1915 28.1983 15.3186 28.4528 13.9954 28.4528C12.3042 28.4528 11.2579 27.9036 10.756 26.8455C10.5127 26.3553 10.4336 26.1257 10.4336 24.8908V17.0996Z" fill="#FFF2EC" />
                                <path d="M22.4453 17.0996H29.6906L29.6085 18.2197H26.6429V28.2635H25.3836V18.2197H22.4453V17.0996Z" fill="#FFF2EC" />
                                <path d="M34.5312 17.0996H35.8118V24.2361C35.8006 24.8082 35.8383 25.3802 35.9243 25.9457C36.116 26.8238 36.9129 27.3109 38.1418 27.3109C39.0543 27.3109 39.7386 27.0689 40.0732 26.6128C40.4078 26.1567 40.4869 25.7502 40.4869 24.7108V17.0996H41.7796V24.8566C41.7796 26.4546 41.6184 26.9883 40.9523 27.5933C40.2861 28.1983 39.401 28.4528 38.0779 28.4528C36.3867 28.4528 35.3403 27.9036 34.8385 26.8455C34.6012 26.3553 34.5343 26.1257 34.5343 24.8908L34.5312 17.0996Z" fill="#FFF2EC" />
                                <path d="M47.628 17.1151H50.18C51.6826 17.1151 52.4126 17.3913 52.9389 17.8319C53.285 18.1109 53.5631 18.4679 53.7515 18.8748C53.9399 19.2817 54.0334 19.7276 54.0247 20.1776C54.0247 22.0144 52.8598 23.2338 51.1199 23.2338H50.9617C51.3788 23.5984 51.7488 24.0154 52.0628 24.475C52.2849 24.7853 52.5738 25.2289 52.9571 25.7812C53.3404 26.3335 54.5844 28.2635 54.5844 28.2635H52.9389C52.7121 27.7176 52.4297 27.1977 52.0963 26.7121C51.4089 25.654 50.7397 24.6767 50.1496 23.9444C49.7177 23.3921 49.4774 23.2401 48.9329 23.2276V28.2573H47.625L47.628 17.1151ZM48.9512 18.2352V22.4054H50.107C51.0834 22.4054 51.6918 22.244 52.0902 21.8345C52.4552 21.4932 52.6469 20.8881 52.6469 20.159C52.6494 19.8017 52.5497 19.4515 52.3601 19.1511C52.1705 18.8508 51.8991 18.6134 51.5792 18.4679C51.1407 18.2977 50.6733 18.2185 50.2044 18.2352H48.9512Z" fill="#FFF2EC" />
                                <path d="M59.9453 17.1803H65.9223L65.7489 18.288H61.2684V21.9028H65.0097V23.0539H61.2684V27.1062H66.0835V28.2635H59.9453V17.1803Z" fill="#FFF2EC" />
                                <path d="M91.0664 17.0996H98.3117L98.2296 18.2197H95.2609V28.2635H93.9986V18.2197H91.0664V17.0996Z" fill="#FFF2EC" />
                                <path d="M106.856 16.9137C107.535 16.8891 108.209 17.0435 108.812 17.362C109.416 17.6805 109.929 18.1523 110.302 18.7319C110.989 19.7776 111.294 21.0776 111.294 22.8711C111.294 25.0182 110.816 26.3679 109.697 27.4074C109.328 27.7668 108.893 28.0473 108.417 28.2322C107.94 28.4172 107.432 28.5028 106.923 28.484C106.334 28.4973 105.749 28.3773 105.211 28.1326C104.673 27.888 104.195 27.5248 103.811 27.0692C102.95 26.0576 102.5 24.5435 102.5 22.6414C102.5 19.0856 104.161 16.9137 106.856 16.9137ZM106.856 17.9872C106.387 17.9693 105.922 18.0801 105.509 18.3083C105.097 18.5365 104.752 18.8735 104.511 19.2842C104.13 20.0196 103.951 20.9845 103.951 22.3684C103.951 24.0408 104.225 25.5084 104.657 26.2562C105.088 27.004 106.092 27.4632 106.983 27.4632C107.375 27.4823 107.766 27.4045 108.122 27.2365C108.477 27.0685 108.788 26.8152 109.027 26.4982C109.569 25.8001 109.824 24.9034 109.824 23.0634C109.824 21.422 109.663 20.3019 109.298 19.5169C109.086 19.0424 108.741 18.6428 108.306 18.3688C107.871 18.0948 107.366 17.9587 106.856 17.9779V17.9872Z" fill="#FFF2EC" />
                                <path d="M116.348 17.1958H118.544C120.22 17.1958 120.977 17.2765 122.051 18.0615C123.295 18.9706 123.903 20.5623 123.903 22.7436C123.903 24.6953 123.392 26.0481 122.659 26.8797C121.701 27.9718 120.919 28.2635 119.213 28.2635H116.357L116.348 17.1958ZM117.656 18.288V27.1527H118.821C119.269 27.1685 119.718 27.1415 120.162 27.072C120.485 27.0178 120.792 26.8939 121.065 26.7088C121.337 26.5237 121.567 26.2815 121.741 25.9985C122.26 25.0526 122.514 23.979 122.474 22.8957C122.474 21.2139 122.267 20.1745 121.534 19.2778C120.801 18.3811 120.113 18.2725 118.964 18.2725L117.656 18.288Z" fill="#FFF2EC" />
                                <path d="M133.08 17.0996L136.7 28.2697H135.218L134.181 24.8349H130.102L129.05 28.2697H127.727L131.377 17.0996H133.08ZM130.464 23.7396H133.846C133.846 23.7396 132.986 20.9471 132.697 19.9976L132.17 18.2694L131.623 19.917C131.565 20.1404 130.464 23.7396 130.464 23.7396Z" fill="#FFF2EC" />
                                <path d="M139.555 17.0996H141.17L143.244 21.3629C143.421 21.7197 143.658 22.2441 143.801 22.604C143.959 22.2316 144.169 21.8035 144.391 21.3629L146.496 17.0996H147.998L144.44 23.9258V28.2697H143.086V23.8389L139.555 17.0996Z" fill="#FFF2EC" />
                                <path d="M80.2675 22.8738C80.1958 23.1419 80.0475 23.3823 79.8416 23.5643C79.6357 23.7463 79.3815 23.8618 79.1111 23.8961C78.8407 23.9305 78.5665 23.8821 78.3231 23.7571C78.0797 23.6322 77.8783 23.4362 77.7442 23.1943C77.6102 22.9523 77.5497 22.6752 77.5704 22.398C77.591 22.1209 77.6919 21.8563 77.8603 21.6378C78.0287 21.4193 78.2569 21.2567 78.516 21.1707C78.775 21.0847 79.0533 21.0792 79.3155 21.1549C79.6647 21.2548 79.9609 21.4918 80.1394 21.8139C80.3178 22.136 80.3639 22.5171 80.2675 22.8738Z" fill="white" />
                                <path d="M76.1806 21.7136C76.0275 22.2711 76.0399 22.8623 76.2163 23.4125C76.3927 23.9628 76.7251 24.4472 77.1715 24.8047C77.6179 25.1621 78.1582 25.3765 78.7241 25.4206C79.2899 25.4646 79.8559 25.3365 80.3503 25.0524C80.8448 24.7683 81.2455 24.3409 81.5017 23.8244C81.758 23.3079 81.8583 22.7255 81.79 22.1508C81.7217 21.5761 81.4877 21.035 81.1178 20.596C80.748 20.1569 80.2587 19.8397 79.712 19.6844C78.9799 19.4764 78.1969 19.5733 77.5348 19.9538C76.8727 20.3343 76.3856 20.9672 76.1806 21.7136Z" fill="white" />
                                <path d="M69.5703 12.2592C69.3792 12.9548 69.3946 13.6926 69.6147 14.3793C69.8347 15.066 70.2494 15.6707 70.8064 16.1168C71.3634 16.563 72.0376 16.8307 72.7437 16.8859C73.4499 16.9412 74.1562 16.7816 74.7734 16.4273C75.3905 16.073 75.8908 15.5399 76.211 14.8955C76.5311 14.2511 76.6567 13.5243 76.5719 12.8071C76.487 12.0899 76.1956 11.4145 75.7343 10.8662C75.2731 10.318 74.6629 9.92166 73.9808 9.7273C73.5282 9.59791 73.0551 9.56078 72.5884 9.61801C72.1218 9.67525 71.6708 9.82574 71.2612 10.0609C70.8516 10.296 70.4914 10.6112 70.2013 10.9884C69.9112 11.3656 69.6967 11.7974 69.5703 12.2592Z" fill="#FF7200" />
                                <path d="M62.3828 5.28096C62.2134 5.90046 62.228 6.55727 62.4246 7.16833C62.6212 7.77939 62.991 8.31726 63.4872 8.71393C63.9835 9.11059 64.5839 9.34824 65.2126 9.3968C65.8413 9.44537 66.4699 9.30268 67.0191 8.98679C67.5683 8.67089 68.0133 8.19596 68.2978 7.62207C68.5824 7.04818 68.6938 6.4011 68.6178 5.76265C68.5419 5.12421 68.282 4.52307 67.8712 4.03526C67.4603 3.54745 66.9169 3.19488 66.3096 3.02212C65.4952 2.79132 64.6244 2.89953 63.8882 3.32303C63.152 3.74652 62.6105 4.45069 62.3828 5.28096Z" fill="#FF7200" />
                                <path d="M53.407 2.0853C53.2589 2.62795 53.2718 3.20323 53.4442 3.73839C53.6165 4.27355 53.9406 4.74456 54.3753 5.09188C54.8101 5.43919 55.336 5.64721 55.8867 5.68964C56.4373 5.73206 56.9879 5.60698 57.4689 5.33022C57.9498 5.05346 58.3396 4.63744 58.5888 4.13476C58.838 3.63208 58.9354 3.06532 58.8689 2.50612C58.8024 1.94692 58.5748 1.4204 58.2149 0.993141C57.8551 0.565876 57.3791 0.257049 56.8472 0.105705C56.4939 0.00517689 56.1246 -0.0233535 55.7605 0.0217334C55.3964 0.0668203 55.0445 0.184646 54.7251 0.368479C54.4056 0.552313 54.1248 0.798556 53.8986 1.09313C53.6725 1.38771 53.5054 1.72485 53.407 2.0853Z" fill="#FF7200" />
                                <path d="M44.2601 2.3829C44.1326 2.84928 44.1435 3.34377 44.2915 3.80383C44.4394 4.26388 44.7178 4.66884 45.0914 4.96751C45.465 5.26618 45.917 5.44513 46.3903 5.48173C46.8636 5.51834 47.3369 5.41097 47.7504 5.17318C48.1639 4.93539 48.4989 4.57787 48.7132 4.14583C48.9275 3.71379 49.0114 3.22663 48.9542 2.74597C48.8971 2.2653 48.7015 1.81271 48.3922 1.44543C48.083 1.07814 47.6738 0.812659 47.2166 0.682554C46.9131 0.595741 46.5957 0.570879 46.2827 0.609387C45.9697 0.647895 45.6672 0.749012 45.3925 0.90696C45.1179 1.06491 44.8765 1.27658 44.6822 1.52984C44.4879 1.78311 44.3444 2.073 44.2601 2.3829Z" fill="#FF7200" />
                                <path d="M36.465 4.89336C36.3579 5.28359 36.3666 5.69745 36.4902 6.08258C36.6138 6.46772 36.8466 6.8068 37.1592 7.0569C37.4717 7.307 37.85 7.45687 38.2461 7.48756C38.6423 7.51824 39.0384 7.42835 39.3844 7.22926C39.7304 7.03018 40.0107 6.73086 40.1899 6.36917C40.3691 6.00749 40.439 5.59972 40.3909 5.19747C40.3427 4.79522 40.1787 4.41657 39.9195 4.10947C39.6603 3.80237 39.3177 3.58061 38.9349 3.47227C38.4226 3.32815 37.8753 3.39673 37.4125 3.66301C36.9497 3.9293 36.609 4.37164 36.465 4.89336Z" fill="#FF7200" />
                                <path d="M30.3729 8.31239C30.2864 8.62571 30.293 8.95815 30.3918 9.26764C30.4907 9.57712 30.6773 9.84972 30.9282 10.0509C31.179 10.2521 31.4827 10.3728 31.8009 10.3978C32.119 10.4228 32.4373 10.3509 32.7154 10.1912C32.9934 10.0316 33.2188 9.79131 33.3629 9.50091C33.507 9.2105 33.5634 8.88299 33.5249 8.55987C33.4865 8.23674 33.3549 7.93254 33.1468 7.68577C32.9387 7.439 32.6635 7.26077 32.356 7.17366C31.9451 7.05806 31.506 7.1129 31.1345 7.32621C30.763 7.53953 30.4892 7.89402 30.3729 8.31239Z" fill="#FF7200" />
                                <path d="M25.31 12.1413C25.2672 12.2984 25.2552 12.4625 25.2747 12.6243C25.2941 12.7861 25.3446 12.9424 25.4233 13.0843C25.5019 13.2262 25.6072 13.3509 25.7331 13.4513C25.859 13.5517 26.0031 13.6258 26.1571 13.6694C26.4681 13.7575 26.8006 13.7159 27.0815 13.5538C27.3625 13.3918 27.5688 13.1225 27.6551 12.8053C27.7414 12.4881 27.7007 12.1488 27.5418 11.8623C27.3829 11.5757 27.119 11.3652 26.808 11.2772C26.497 11.1891 26.1645 11.2307 25.8835 11.3927C25.6026 11.5548 25.3963 11.8241 25.31 12.1413Z" fill="#FF7200" />
                                <path d="M77.6201 22.123C77.7174 21.765 77.95 21.4611 78.2669 21.2781C78.4238 21.1875 78.5966 21.1293 78.7756 21.1068C78.9545 21.0843 79.1361 21.098 79.3098 21.1471C79.4836 21.1962 79.6461 21.2798 79.7883 21.393C79.9304 21.5062 80.0492 21.6468 80.1381 21.8068C80.2269 21.9669 80.2839 22.1432 80.306 22.3258C80.328 22.5083 80.3146 22.6935 80.2664 22.8708C80.1692 23.2287 79.9366 23.5326 79.6197 23.7156C79.3028 23.8986 78.9277 23.9457 78.5768 23.8466C78.2258 23.7474 77.9279 23.5101 77.7485 23.1869C77.5691 22.8636 77.5229 22.4809 77.6201 22.123Z" fill="white" />
                                <path d="M82.8313 23.5845C83.0432 22.8118 83.0257 21.9924 82.781 21.2299C82.5363 20.4674 82.0753 19.7961 81.4565 19.3009C80.8376 18.8057 80.0887 18.5089 79.3044 18.4481C78.5201 18.3872 77.7358 18.565 77.0506 18.959C76.3655 19.353 75.8103 19.9455 75.4553 20.6614C75.1003 21.3774 74.9615 22.1847 75.0564 22.9811C75.1513 23.7776 75.4757 24.5275 75.9885 25.1358C76.5013 25.7442 77.1795 26.1837 77.9372 26.3987C78.4397 26.5443 78.9655 26.5869 79.4842 26.524C80.003 26.4612 80.5044 26.2941 80.9594 26.0324C81.4145 25.7707 81.8142 25.4197 82.1355 24.9995C82.4568 24.5794 82.6933 24.0984 82.8313 23.5845Z" fill="#FF7200" />
                                <path d="M88.4336 32.7377C88.6247 32.0421 88.6093 31.3042 88.3892 30.6176C88.1692 29.9309 87.7545 29.3262 87.1975 28.88C86.6405 28.4338 85.9663 28.1662 85.2602 28.1109C84.5541 28.0557 83.8477 28.2153 83.2305 28.5696C82.6134 28.9239 82.1131 29.4569 81.7929 30.1013C81.4728 30.7457 81.3472 31.4725 81.432 32.1898C81.5169 32.907 81.8084 33.5824 82.2696 34.1306C82.7308 34.6788 83.3411 35.0752 84.0231 35.2696C84.4757 35.3994 84.9489 35.4368 85.4157 35.3798C85.8825 35.3227 86.3337 35.1723 86.7434 34.9371C87.1531 34.7019 87.5132 34.3866 87.8033 34.0092C88.0933 33.6318 88.3075 33.1997 88.4336 32.7377Z" fill="#FF7200" />
                                <path d="M95.6174 39.7159C95.787 39.0963 95.7725 38.4393 95.5759 37.8281C95.3794 37.2168 95.0095 36.6788 94.5131 36.2821C94.0167 35.8853 93.416 35.6477 92.7872 35.5992C92.1584 35.5507 91.5296 35.6936 90.9804 36.0098C90.4312 36.326 89.9863 36.8012 89.7019 37.3754C89.4175 37.9496 89.3065 38.5969 89.3828 39.2354C89.4592 39.874 89.7195 40.4751 90.1308 40.9627C90.5422 41.4503 91.086 41.8025 91.6936 41.9747C92.0967 42.0898 92.518 42.1227 92.9335 42.0714C93.349 42.0201 93.7505 41.8857 94.115 41.6759C94.4795 41.466 94.7998 41.1849 95.0576 40.8486C95.3155 40.5122 95.5057 40.1273 95.6174 39.7159Z" fill="#FF7200" />
                                <path d="M104.599 42.9117C104.748 42.3691 104.735 41.7938 104.562 41.2587C104.39 40.7235 104.066 40.2525 103.631 39.9052C103.196 39.5579 102.67 39.3498 102.12 39.3074C101.569 39.265 101.019 39.3901 100.538 39.6668C100.057 39.9436 99.6669 40.3596 99.4177 40.8623C99.1685 41.365 99.071 41.9317 99.1375 42.4909C99.2041 43.0501 99.4317 43.5766 99.7915 44.0039C100.151 44.4312 100.627 44.74 101.159 44.8913C101.873 45.0935 102.636 44.9986 103.281 44.6274C103.926 44.2563 104.4 43.6393 104.599 42.9117Z" fill="#FF7200" />
                                <path d="M113.743 42.614C113.87 42.1475 113.86 41.6528 113.712 41.1926C113.564 40.7323 113.285 40.3272 112.911 40.0285C112.538 39.7297 112.085 39.5508 111.612 39.5143C111.139 39.4778 110.665 39.5854 110.252 39.8234C109.838 40.0615 109.503 40.4193 109.289 40.8516C109.075 41.284 108.991 41.7713 109.049 42.2521C109.106 42.7329 109.303 43.1855 109.612 43.5526C109.922 43.9196 110.332 44.1847 110.789 44.3143C111.402 44.4878 112.057 44.4063 112.611 44.0875C113.164 43.7687 113.572 43.2387 113.743 42.614Z" fill="#FF7200" />
                                <path d="M121.538 40.1007C121.644 39.711 121.634 39.298 121.51 38.9139C121.385 38.5298 121.152 38.1919 120.84 37.943C120.528 37.694 120.15 37.5452 119.754 37.5152C119.359 37.4853 118.964 37.5757 118.619 37.7749C118.274 37.9741 117.994 38.2732 117.816 38.6344C117.638 38.9956 117.568 39.4026 117.617 39.804C117.665 40.2054 117.829 40.5831 118.088 40.8895C118.347 41.1958 118.689 41.4169 119.071 41.5249C119.324 41.597 119.59 41.6174 119.851 41.5848C120.112 41.5522 120.365 41.4673 120.594 41.335C120.823 41.2027 121.024 41.0256 121.186 40.8138C121.348 40.602 121.468 40.3597 121.538 40.1007Z" fill="#FF7200" />
                                <path d="M127.633 36.6844C127.719 36.3712 127.712 36.039 127.614 35.7296C127.515 35.4203 127.328 35.1479 127.077 34.9468C126.827 34.7457 126.523 34.625 126.205 34.5999C125.887 34.5748 125.569 34.6465 125.291 34.8059C125.013 34.9653 124.788 35.2052 124.643 35.4953C124.499 35.7854 124.442 36.1127 124.48 36.4357C124.518 36.7587 124.649 37.063 124.857 37.3099C125.065 37.5569 125.339 37.7355 125.647 37.8232C126.058 37.9397 126.498 37.8853 126.87 37.6719C127.242 37.4585 127.517 37.1035 127.633 36.6844Z" fill="#FF7200" />
                                <path d="M129.95 32.8187C129.886 33.0553 129.892 33.306 129.968 33.539C130.043 33.7721 130.185 33.9771 130.375 34.128C130.565 34.279 130.794 34.3692 131.034 34.3871C131.274 34.4051 131.514 34.35 131.723 34.2289C131.932 34.1078 132.102 33.9261 132.21 33.7068C132.318 33.4875 132.36 33.2404 132.33 32.9968C132.301 32.7531 132.201 32.5239 132.044 32.3381C131.886 32.1523 131.679 32.0183 131.447 31.953C131.136 31.8654 130.803 31.9074 130.523 32.0697C130.242 32.2321 130.036 32.5014 129.95 32.8187Z" fill="#FF7200" />
                            </g>
                            <defs>
                                <clipPath id="clip0_3441_155493">
                                    <rect width="148" height="45" fill="white" />
                                </clipPath>
                            </defs>
                        </svg>
                    </LogoBlock>
            </BotWrapper>
            <Wrapper ref={wrapperRef}>
                <AnimatePresence initial={false}>
                    {!isFixed && (
                        <>
                            <CompasBlock 
                                initial={{x: '-50%', y: -500, opacity: 0}} 
                                exit={{y: -500, opacity: 0, x: '-50%'}}
                                animate={{y: 0, opacity: 1, x: '-50%'}}
                                transition={{duration: 0.4, ease: 'linear'}}
                            >
                                <CompasHeader>Это <ColoredSpan>навигатор профессий — интерактивный помощник</ColoredSpan> для студентов колледжей и техникумов</CompasHeader>
                                <SmallText>
                                    Мы создали его, чтобы помочь тебе понять свою специальность, узнать, как вырасти в профессии и найти первую работу {'\n\n'}
                                    погрузись в мир реальных вакансий, лайфхаков и советов от ведущих работодателей!
                                </SmallText>
                            </CompasBlock>
                            <TipBlock 
                                initial={{ y: -500, opacity: 0}} 
                                exit={{y: -500, opacity: 0, }}
                                animate={{y: 0, opacity: 1,}}
                                transition={{duration: 0.4, ease: 'linear'}}
                            >
                                <SmallText>
                                    листай ниже, чтобы посмотреть все профессии
                                </SmallText>
                                <Arrow width="15" height="27" viewBox="0 0 15 27" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M8.36328 1C8.36328 0.447715 7.91557 -2.41411e-08 7.36328 0C6.811 2.41411e-08 6.36328 0.447715 6.36328 1L7.36328 1L8.36328 1ZM6.65618 26.7071C7.0467 27.0976 7.67986 27.0976 8.07039 26.7071L14.4343 20.3431C14.8249 19.9526 14.8249 19.3195 14.4343 18.9289C14.0438 18.5384 13.4107 18.5384 13.0201 18.9289L7.36328 24.5858L1.70643 18.9289C1.3159 18.5384 0.682739 18.5384 0.292214 18.9289C-0.0983101 19.3195 -0.0983101 19.9526 0.292214 20.3431L6.65618 26.7071ZM7.36328 1L6.36328 1L6.36328 26L7.36328 26L8.36328 26L8.36328 1L7.36328 1Z" fill="#FF7F00"/>
                                </Arrow>
                            </TipBlock>
                        </>
                    )}
                </AnimatePresence>
                <ContentWrapper>
                    <Clouds animationProps={animationProps} isFixed={isFixed}/>
                    <TitleStyled ref={titleRef} 
                        animate={{opacity: +(!isFixed)}}
                    >
                        <ColoredSpan>навигатор</ColoredSpan>{'\n'}профессий
                    </TitleStyled>
                    <Stones animationProps={animationProps} isFixed={isFixed}/>
                    <JobsBlock handleMouseEnter={handleMouseEnter} handleMouseLeave={handleMouseLeave} animationProps={animationProps}/>
                    <FooterText>
                        © 2005-2026 FutureToday.{'\n'}Все права защищены.
                    </FooterText>
                    <BotFooterText>
                        <SmallText>группа вк</SmallText>
                        <svg width="27" height="15" viewBox="0 0 27 15" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M1 6.36395C0.447715 6.36395 9.65645e-08 6.81167 0 7.36395C-9.65645e-08 7.91624 0.447715 8.36395 1 8.36395L1 7.36395L1 6.36395ZM26.7071 8.07106C27.0976 7.68054 27.0976 7.04737 26.7071 6.65685L20.3431 0.292888C19.9526 -0.0976362 19.3195 -0.0976364 18.9289 0.292888C18.5384 0.683412 18.5384 1.31658 18.9289 1.7071L24.5858 7.36396L18.9289 13.0208C18.5384 13.4113 18.5384 14.0445 18.9289 14.435C19.3195 14.8255 19.9526 14.8255 20.3431 14.435L26.7071 8.07106ZM1 7.36395L1 8.36395L26 8.36396L26 7.36396L26 6.36396L1 6.36395L1 7.36395Z" fill="#FFF2EC"/>
                        </svg>

                        <button onClick={openVk}>
                            <svg width="40" height="40" viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <g clipPath="url(#clip0_3441_155136)">
                                <path d="M0 19.2C0 10.149 0 5.62355 2.81177 2.81177C5.62355 0 10.149 0 19.2 0H20.8C29.851 0 34.3764 0 37.1882 2.81177C40 5.62355 40 10.149 40 19.2V20.8C40 29.851 40 34.3764 37.1882 37.1882C34.3764 40 29.851 40 20.8 40H19.2C10.149 40 5.62355 40 2.81177 37.1882C0 34.3764 0 29.851 0 20.8V19.2Z" fill="#FFF2EC"/>
                                <path d="M21.2833 28.8167C12.1666 28.8167 6.96667 22.5667 6.75 12.1667H11.3167C11.4667 19.8001 14.8333 23.0334 17.4999 23.7001V12.1667H21.8001V18.7501C24.4334 18.4667 27.1997 15.4667 28.1331 12.1667H32.4332C31.7165 16.2334 28.7165 19.2334 26.5832 20.4667C28.7165 21.4667 32.1333 24.0834 33.4333 28.8167H28.6998C27.6832 25.6501 25.1501 23.2001 21.8001 22.8667V28.8167H21.2833Z" fill="#1E1E1E"/>
                                </g>
                                <defs>
                                <clipPath id="clip0_3441_155136">
                                <rect width="40" height="40" fill="white"/>
                                </clipPath>
                                </defs>
                            </svg>
                        </button>
                    </BotFooterText>
                </ContentWrapper>  
            </Wrapper>
         </>
    )
}